import React from 'react';
import Image from 'next/image';

import Loader from '../../Loader';
import { SingleSelect, MinMaxSelect, MultiSelect } from '../../Selectors';
import handleMultiSelect from '../../../helper/handle_multiselect';
import { 
  fuelTypeOptions, CURRENT_YEAR,
} from '../../../helper/filters';
import useBestViewHeight from '../../Hooks/BestViewHeight';
import { event } from '../../../lib/gtag';

import IOption from "../../../interfaces/IOption";
import iFilters from "../../../interfaces/iFilters";

import styles from '../styles.module.scss'
import SearchIcon from '../../../public/assets/icons/search-black.svg';
import { useIsBoiWebsite } from "../../Context/WebsiteType";

interface iVehicleFilters {
  filters: iFilters,
  resetFilters(): void,
  handleSelectChange(value: any, name: string): void,
  processingURL?: boolean,
  options: { [key: string]: IOption[] },
  onClose?: () => void,
}

export default function SweepVehicleFilters({ 
  filters, handleSelectChange, resetFilters, 
  processingURL = false, options, onClose
}: iVehicleFilters) {
  
  const isBoiWebsite = useIsBoiWebsite();

  useBestViewHeight()

  if (processingURL) return (
    <div className={styles.filtersHolder}>
      <p>Loading filters...</p>
      <Loader />
    </div>
  )

  const {
    makes, models, bodyTypes, locationStates, colors,
    minPrices, maxPrices, minYears, maxYears, minMileages, maxMileages
  } = options

  return (
    <div className={styles.filtersHolder}>
      <div className={styles.filters}>
        <SingleSelect
          title="Fuel Type" 
          value={filters.fuel_ids}
          placeholder="Both (BEV & PHEV)"
          options={fuelTypeOptions} 
          rounded={isBoiWebsite}
          onChange={(value: any) => handleSelectChange(value, "fuel_ids")}
          onClick={() => {event('clickedOnFuelTypeDropdown')}}
        />

        <MultiSelect
          title='Make'
          values={filters.manufacturer_ids}
          options={makes}
          rounded={isBoiWebsite}
          onClick={() => {event('clickedOnMakeDropdown')}}
          onChange={(values: any) => {
            const selected = handleMultiSelect(values)
            handleSelectChange(selected, "manufacturer_ids")
          }}
        />

        <MultiSelect 
          title='Model'
          values={filters.model_ids}
          options={models}
          rounded={isBoiWebsite}
          onClick={() => {event('clickedOnModelDropdown')}}
          onChange={(values: any) => {
            const selected = handleMultiSelect(values)
            handleSelectChange(selected, "model_ids")
          }}
        />

        <MultiSelect 
          title='Body'
          values={filters.body_ids}
          options={bodyTypes}
          rounded={isBoiWebsite}
          onClick={() => {event('clickedOnBodyTypeDropdown')}}
          onChange={(values: any) => {
            const selected = handleMultiSelect(values)
            handleSelectChange(selected, "body_ids")
          }}
        />

        <MultiSelect 
          title='Color'
          values={filters.colors}
          options={colors}
          rounded={isBoiWebsite}
          onClick={() => {event('clickedOnColorDropdown')}}
          onChange={(values: any) => {
            const selected = handleMultiSelect(values)
            handleSelectChange(selected, "colors")
          }}
        />

        <MinMaxSelect 
          title='Price'
          minPlaceholder='€0'
          minValue={filters.min_price}
          minOptions={minPrices}
          onMinChange={(value: any) => handleSelectChange(value, "min_price")}
          maxValue={filters.max_price}
          maxOptions={maxPrices}
          onMaxChange={(value: any) => handleSelectChange(value, "max_price")}
          onMinClick={() => {event('clickedOnMinPriceDropdown')}}
          onMaxClick={() => {event('clickedOnMaxPriceDropdown')}}
        />

        <MinMaxSelect 
          title='Year'
          minPlaceholder='<2000'
          maxPlaceholder={`${CURRENT_YEAR}`}
          minValue={filters.min_year}
          minOptions={minYears}
          onMinChange={(value: any) => handleSelectChange(value, "min_year")}
          maxValue={filters.max_year}
          maxOptions={maxYears}
          onMaxChange={(value: any) => handleSelectChange(value, "max_year")}
          onMinClick={() => {event('clickedOnMinYearDropdown')}}
          onMaxClick={() => {event('clickedOnMaxYearDropdown')}}
        />
        
        <MinMaxSelect 
          title='Mileage'
          minPlaceholder='0 km'
          minValue={filters.min_mileage}
          minOptions={minMileages}
          onMinChange={(value: any) => handleSelectChange(value, "min_mileage")}
          maxValue={filters.max_mileage}
          maxOptions={maxMileages}
          onMaxChange={(value: any) => handleSelectChange(value, "max_mileage")}
          onMinClick={() => {event('clickedOnMinMileageDropdown')}}
          onMaxClick={() => {event('clickedOnMaxMileageDropdown')}}
        />

        <MultiSelect
          title='Location'
          values={filters.location_states}
          options={locationStates}
          allPlaceholder='All Counties'
          rounded={isBoiWebsite}
          onClick={() => {event('clickedOnLocationDropdown')}}
          onChange={(values: any) => {
            const selected = handleMultiSelect(values)
            handleSelectChange(selected, "location_states")
          }}
        />
      </div>

      <div 
        className={`${isBoiWebsite ? `${styles.searchButtonBoi} light-blue-button-boi` : `${styles.searchButton} blue-button`}`}
        onClick={() => {
          event('clickedOnSearchButton')
          if (onClose) onClose()
        }}
      >
        <span className={isBoiWebsite ? styles.searchTextBoi : styles.searchText}>Search</span>
        <Image src={SearchIcon} alt="Search icon" width={18}/>
      </div>

      <p className={`${isBoiWebsite ? `${styles.resetFiltersBoi} light-blue-button-boi` : styles.resetFilters}`} onClick={() => {
        event('clickedOnResetButton')
        resetFilters()
      }}>
        Reset Filters
      </p>
    </div>
  )
};